/**
 * DEC-067 decision modal: "AI credits exhausted: pause or continue static-only?"
 *
 * Built on the shared `Modal` (DEC-197). Shown when the scan pauses with llm_mode="awaiting_decision" (via the SSE
 * llm_unavailable event, or on reload from the scan status). Never
 * auto-dismisses; ESC/secondary = stay paused (user keeps control).
 */
import { AGENT_LABELS } from '../../types/api'
import { Modal } from '../shared/Modal'

interface LlmDecisionModalProps {
  reason: string
  fromAgent: string
  busy: boolean
  onContinueStatic: () => void
  onStayPaused: () => void
}

const REASON_TEXT: Record<string, { title: string; detail: string }> = {
  credit_exhausted: {
    title: 'AI credits exhausted',
    detail:
      'The Anthropic API reported that the credit balance is too low. No further AI calls can succeed until the account is topped up.',
  },
  auth_invalid: {
    title: 'AI access rejected',
    detail:
      'The Anthropic API rejected the configured API key. No further AI calls can succeed until the key is fixed.',
  },
}

export function LlmDecisionModal({
  reason,
  fromAgent,
  busy,
  onContinueStatic,
  onStayPaused,
}: LlmDecisionModalProps) {
  const text = REASON_TEXT[reason] ?? {
    title: 'AI became unavailable',
    detail: reason,
  }
  const agentLabel = AGENT_LABELS[fromAgent] ?? fromAgent

  return (
    <Modal title={text.title} onClose={onStayPaused} dot="rgb(var(--warn))" maxWidth={460} dismissible={false}>
        <p className="text-[13px] text-body leading-relaxed mb-2">
          {text.detail} Detected while <span className="font-medium">{agentLabel}</span> was
          running; the scan is now <span className="font-medium">paused</span>.
        </p>
        <p className="text-[13px] text-body leading-relaxed mb-5">
          You can continue static-only (every remaining agent runs its non-AI analysis and the
          report clearly labels the boundary), or stay paused, top up, and resume with full AI.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={onContinueStatic}
            disabled={busy}
            className="w-full py-2.5 rounded-md bg-accent text-white font-semibold text-[13px] hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {busy ? 'Continuing…' : 'Continue static-only to completion'}
          </button>
          <button
            onClick={onStayPaused}
            disabled={busy}
            className="w-full py-2.5 rounded-md border border-hairline text-body font-medium text-[13px] hover:bg-ground disabled:opacity-50 transition-colors"
          >
            Stay paused, I&apos;ll top up and resume with AI
          </button>
        </div>
    </Modal>
  )
}
